import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {APageLoader, useATheme} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {t} from "i18next";
import {isEmpty} from "lodash-es";
import {Chart} from "regraph";
import {useSelector} from "react-redux";
import EmptyContent from "components/EmptyContent";
import {InvestigationDetailContext} from "contexts/InvestigationDetailContext";
import {RelationsGraphContext} from "contexts/RelationsGraphContext";
import {
  selectHasGraphItems,
  selectInvestigationGraphGroups,
  selectInvestigationGraphInitialized,
  selectInvestigationGraphLoading
} from "store/selectors/investigationSelectors";
import {
  GRAPH_THEME_OPTIONS,
  PING_OPTIONS
} from "utils/graph/graphStyleConstants";
import {
  getComboStyle,
  handleItemInteraction,
  setNodeStyle
} from "utils/graph/graphStyleUtils";
import {
  DEFAULT_ANNOTATION_COORDS,
  getRelationsGraphOptions,
  isEdge,
  isNode
} from "utils/graph/graphUtils";
import Annotation from "./Annotation";
import GraphToolbar from "./GraphToolbar/GraphToolbar";
import css from "./Graph.module.scss";

const Graph = () => {
  const chartRef = useRef();

  const {currentTheme, isLight} = useATheme();

  const isLoading = useSelector(selectInvestigationGraphLoading);
  const graphInitialized = useSelector(selectInvestigationGraphInitialized);
  const graphGroups = useSelector(selectInvestigationGraphGroups);
  const hasGraphItems = useSelector(selectHasGraphItems);

  const {isFullscreen} = useContext(InvestigationDetailContext);
  const {
    graphModel,
    layout,
    openCombos,
    combine,
    setLayout,
    setOpenCombos,
    setCombine
  } = useContext(RelationsGraphContext);

  const [currentSelection, setCurrentSection] = useState({});
  const [annotationPosition, setAnnotationPosition] = useState({});
  const [zoom, setZoom] = useState(1);
  const [positions, setPositions] = useState({});
  const [offset, setOffset] = useState(DEFAULT_ANNOTATION_COORDS);
  const [isDragging, setIsDragging] = useState(false);
  const [handMode, setHandMode] = useState(true);

  /**
   * NOTE: graphModel !== graphViewModel
   * graphModel: Provided by Context. Entire Data model for the graph.
   * graphViewModel: This is a modified version of `graphModel`. We attach Node styling
   *   based on the currentTheme. May also be used in future to provide a viewModel filtering
   *   'graphModel' to a subgraph.
   *
   * Important to note, `graphModel` should not be modified by this class, only reference it to source
   *   original dataset. `graphViewModel` is the model modified within this component and
   *   provided to the graph component. This ViewModel is not shared outside this component.
   */
  const [graphViewModel, setGraphViewModel] = useState({});

  const themeOptions = isLight
    ? GRAPH_THEME_OPTIONS.LIGHT
    : GRAPH_THEME_OPTIONS.DARK;

  const graphOptions = useMemo(
    () => getRelationsGraphOptions(themeOptions, handMode),
    [themeOptions, handMode]
  );

  useEffect(() => {
    // Updates Nodes with styling properties, only when `graphModel` or
    // `currentTheme` change. We don't want to do this one every render.
    setGraphViewModel(setNodeStyle(graphModel, currentTheme));
  }, [graphModel, currentTheme]);

  useEffect(() => {
    // Force re-run styling on Combo's when theme changes.
    setCombine({...combine});
  }, [currentTheme]);

  useEffect(() => {
    // Reset graph when investigation data model changes.
    setAnnotationPosition(null);
    setCurrentSection({});
  }, [graphModel]);

  useEffect(() => {
    // Re-fit to view when isFullscreen changes.
    handleFit("all");
  }, [isFullscreen, setLayout]);

  /**
   * Shows a Halo when user clicks on a node.
   */
  const handleNodeClick = ({id}) => {
    if (chartRef.current && id) {
      chartRef.current.ping({[id]: true}, PING_OPTIONS);
    }
  };

  /**
   * Handle a change to Graph position or selections
   */
  const handleChange = async ({
    positions: newPositions,
    selection: newSelection
  }) => {
    const chart = chartRef.current;

    if (!chart || (!newPositions && !newSelection)) {
      return;
    }

    const nextPositions = newPositions || positions;
    let nextSelection = currentSelection;
    let nextAnnotationPosition = annotationPosition;

    if (!isEmpty(newSelection)) {
      const selectedItemId = getFirstSelection(newSelection);
      if (selectedItemId) {
        const selectedItem = graphViewModel[selectedItemId];
        if (isNode(selectedItem)) {
          nextSelection = {[selectedItemId]: true};
          nextAnnotationPosition = chart.viewCoordinates(
            nextPositions[selectedItemId].x,
            nextPositions[selectedItemId].y
          );
        }
      }
    } else if (!annotationPosition && !nextPositions) {
      const firstItemId = getFirstSelection(graphViewModel);
      nextAnnotationPosition = chart.viewCoordinates(
        nextPositions[firstItemId].x,
        nextPositions[firstItemId].y
      );
      nextSelection = {[firstItemId]: true};
    } else {
      nextSelection = {};
    }

    setAnnotationPosition(nextAnnotationPosition);
    setCurrentSection(nextSelection);
    setPositions(nextPositions);
  };

  const handleCombineNodes = ({setStyle, id, nodes, combo}) => {
    setStyle(
      getComboStyle(id, nodes, combo, graphGroups, openCombos, currentTheme)
    );
  };

  /**
   * Handler for doubling clicking a node.
   * If node is a combo group, expand/collapse the node group.
   */
  const handleDoubleClick = ({id}) => {
    setOpenCombos({...openCombos, [id]: !openCombos[id]});
    setCombine({...combine});
  };

  /**
   * Store the offset to the cursor position to correct during a drag
   */
  const handlePointerDown = ({id, x, y}) => {
    const item = graphViewModel[id];
    const chart = chartRef.current;
    if (!id || isEdge(item) || !chart) {
      return;
    }

    const {x: currentX, y: currentY} = chart.viewCoordinates(
      positions[id].x,
      positions[id].y
    );

    setOffset({
      x: x - currentX,
      y: y - currentY
    });
  };

  /**
   * Given a set of selected nodes (records). Return the first key.
   * @param {Object} selectedItems Selected nodes object.
   * @returns {String} First key within 'records'
   */
  const getFirstSelection = (selectedItems) => {
    if (!selectedItems) {
      return null;
    }
    const keys = Object.keys(selectedItems);
    return keys[0];
  };

  /**
   * Handler for Drag Start event, update Annotation position.
   */
  const handleDragStart = ({type, x, y, draggedItems}) => {
    if (
      type !== "node" ||
      getFirstSelection(draggedItems) !== getFirstSelection(currentSelection)
    ) {
      return;
    }

    setIsDragging(true);
    setAnnotationPosition({
      x: x - offset.x,
      y: y - offset.y
    });
  };

  /**
   * Handler for Drag End event.
   * Set final Annotation position if there is a selection.
   */
  const handleDragEnd = ({defaultPrevented}) => {
    const chart = chartRef.current;
    const selectedItemId = getFirstSelection(currentSelection);
    if (
      !defaultPrevented ||
      !chart ||
      !selectedItemId ||
      !positions[selectedItemId]
    ) {
      setIsDragging(false);
    }

    if (selectedItemId && positions) {
      const annotationPosition = chart.viewCoordinates(
        positions[selectedItemId].x,
        positions[selectedItemId].y
      );
      setAnnotationPosition(annotationPosition);
    }
    setIsDragging(false);
  };

  /**
   * Handle view (zoom) level change.
   */
  const handleViewChange = ({zoom}) => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    const selectedItemId = getFirstSelection(currentSelection);
    if (zoom === zoom && (!selectedItemId || !positions[selectedItemId])) {
      return;
    }

    let annotationPositionClone = annotationPosition;
    if (selectedItemId && positions[selectedItemId]) {
      annotationPositionClone = chart.viewCoordinates(
        positions[selectedItemId].x,
        positions[selectedItemId].y
      );
    }

    setZoom(zoom);
    setAnnotationPosition(annotationPositionClone);
  };

  const handleWheelEvent = ({preventDefault}) => {
    if (isDragging) {
      preventDefault();
    }
  };

  const handleFit = (fitType) => {
    if (chartRef.current) {
      chartRef.current.fit(fitType);
    }
  };

  const handleZoom = (inOrOut) => {
    if (chartRef.current) {
      chartRef.current.zoom(inOrOut);
    }
  };

  const selectedItemId = getFirstSelection(currentSelection);

  return (
    <div
      className={classNames(css.graphContainer, {
        [css.graphContainerFullscreen]: isFullscreen
      })}
      data-testid={"InvestigationDetail-RelationsGraph"}>
      <Chart
        ref={chartRef}
        items={graphViewModel}
        options={{...graphOptions}}
        combine={combine}
        layout={layout}
        onClick={handleNodeClick}
        onCombineNodes={handleCombineNodes}
        onDoubleClick={handleDoubleClick}
        onChange={handleChange}
        onDrag={handleDragStart}
        onDragEnd={handleDragEnd}
        onPointerDown={handlePointerDown}
        onViewChange={handleViewChange}
        onWheel={handleWheelEvent}
        onItemInteraction={handleItemInteraction}
      />

      <GraphToolbar
        handMode={handMode}
        setHandMode={setHandMode}
        handleFit={handleFit}
        handleZoom={handleZoom}
      />

      {selectedItemId &&
        graphViewModel[selectedItemId] &&
        annotationPosition && (
          <Annotation
            position={annotationPosition}
            info={graphViewModel[selectedItemId].data}
            size={graphViewModel[selectedItemId].size}
            zoom={zoom}
          />
        )}

      {/** isReady indicates if we've got a positive result from the
       * Status API yet, so show loading to not keep user in suspence.
       * Spinner here per //Spinner here per https://magnetic.cisco.com/0a43ab5cd/p/43b10f-progress-and-loading-indicators
       */}
      {isLoading && (
        <div className={css.graphLoader}>
          <APageLoader className={css.graphLoaderSpinner} />
        </div>
      )}

      {/** Note: I'm referencing hasGraphItems here, instead of 'graphNodeCount' because Store properties
       * all update together whereas contexts values will update after. Mixing Store & Context props
       * to resolve a result in the same render will cause an unintended flash of content.
       * Best way to make work is either reference all Store props, or provide the store props, via the Context.
       */}
      {graphInitialized && !isLoading && !hasGraphItems && (
        <EmptyContent
          className={css.emptyState}
          name={t("investigationDetail.graph.emptyName")}
        />
      )}
    </div>
  );
};

export default Graph;
